import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Confetti from 'react-confetti';
import axios from 'axios';
import {
  TrophyIcon,
  SparklesIcon,
  XMarkIcon,
  GiftIcon,
  CurrencyDollarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import { useLanguage } from '../../contexts/LanguageContext';

const ease = [0.22, 1, 0.36, 1];

function PromoSlide({ promotion, copy, formatMessage }) {
  const PrizeIcon = promotion.prize_type === 'cash' ? CurrencyDollarIcon : GiftIcon;
  return (
    <div className="overflow-hidden rounded-xl border border-secondary-200/80 bg-white">
      <div className="relative flex aspect-[16/10] items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50 p-4">
        {promotion.image_url ? (
          <img
            src={promotion.image_url}
            alt={promotion.prize_name}
            className="h-full w-full object-contain"
            draggable={false}
          />
        ) : (
          <PrizeIcon className="h-12 w-12 text-orange-500" />
        )}
        <span className="absolute left-3 top-3 rounded-full bg-orange-100 px-2.5 py-0.5 text-2xs font-bold uppercase tracking-wide text-orange-700 ring-1 ring-orange-200">
          {formatMessage(copy.required || '{count} referrals', { count: promotion.required_referrals })}
        </span>
      </div>
      <div className="border-t border-secondary-100 px-4 py-3 text-left">
        <p className="text-sm font-bold text-secondary-900 sm:text-base">{promotion.title}</p>
        <p className="mt-0.5 text-xs text-secondary-500 sm:text-sm">{promotion.prize_name}</p>
      </div>
    </div>
  );
}

function PromoCarousel({ promotions, copy, formatMessage }) {
  const [[index, direction], setSlide] = useState([0, 1]);
  const pausedRef = useRef(false);
  const count = promotions.length;
  const currentIndex = count > 0 ? ((index % count) + count) % count : 0;

  const paginate = useCallback((nextDirection) => {
    setSlide(([prevIndex]) => [prevIndex + nextDirection, nextDirection]);
  }, []);

  const goTo = useCallback((target) => {
    setSlide(([prevIndex]) => {
      const prev = count > 0 ? ((prevIndex % count) + count) % count : 0;
      return [target, target >= prev ? 1 : -1];
    });
  }, [count]);

  useEffect(() => {
    if (count <= 1) return undefined;
    const id = window.setInterval(() => {
      if (!pausedRef.current) paginate(1);
    }, 5000);
    return () => window.clearInterval(id);
  }, [count, paginate]);

  if (count === 0) return null;

  const current = promotions[currentIndex];
  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 64 : -64, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -64 : 64, opacity: 0 }),
  };

  return (
    <div
      className="relative mx-auto mt-6 w-full max-w-md"
      onPointerEnter={() => { pausedRef.current = true; }}
      onPointerLeave={() => { pausedRef.current = false; }}
    >
      <div className="relative overflow-hidden">
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={current.id}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.28, ease }}
            drag={count > 1 ? 'x' : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.12}
            onDragEnd={(_, info) => {
              if (info.offset.x < -40 || info.velocity.x < -300) paginate(1);
              else if (info.offset.x > 40 || info.velocity.x > 300) paginate(-1);
            }}
            className="cursor-grab active:cursor-grabbing"
          >
            <PromoSlide promotion={current} copy={copy} formatMessage={formatMessage} />
          </motion.div>
        </AnimatePresence>

        {count > 1 && (
          <>
            <button
              type="button"
              onClick={() => paginate(-1)}
              aria-label={copy.prev || 'Previous'}
              className="absolute left-2 top-1/2 z-10 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-secondary-600 shadow-soft ring-1 ring-secondary-200 transition hover:bg-white hover:text-orange-600"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => paginate(1)}
              aria-label={copy.next || 'Next'}
              className="absolute right-2 top-1/2 z-10 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-secondary-600 shadow-soft ring-1 ring-secondary-200 transition hover:bg-white hover:text-orange-600"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="mt-3 flex items-center justify-center gap-2">
          {promotions.map((promotion, dotIndex) => (
            <button
              key={promotion.id}
              type="button"
              onClick={() => goTo(dotIndex)}
              aria-label={`${dotIndex + 1} / ${count}`}
              aria-current={dotIndex === currentIndex}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                dotIndex === currentIndex ? 'w-6 bg-orange-500' : 'w-1.5 bg-secondary-300 hover:bg-secondary-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PromotionsIntroModal({ onClose, onViewPromotions, promotions: initialPromotions = null }) {
  const { t, formatMessage } = useLanguage();
  const copy = t.promotionsIntro || {};
  const [promotions, setPromotions] = useState(initialPromotions);
  const [loading, setLoading] = useState(!initialPromotions);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const onResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (initialPromotions) return undefined;
    let cancelled = false;
    axios
      .get('/api/client/promotions/intro', { withCredentials: true })
      .then((response) => {
        if (!cancelled) setPromotions(response.data?.promotions || []);
      })
      .catch(() => {
        if (!cancelled) setPromotions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initialPromotions]);

  const markIntroSeen = async () => {
    try {
      await axios.post('/api/client/promotions/intro/seen', {}, { withCredentials: true });
    } catch (error) {
      console.error('Error marking promotions intro seen:', error);
    }
  };

  const handleView = async () => {
    await markIntroSeen();
    onViewPromotions?.();
  };

  const handleClose = async () => {
    await markIntroSeen();
    onClose?.();
  };

  // If there is nothing to show, release the caller (onboarding / dashboard).
  useEffect(() => {
    if (!loading && (!promotions || promotions.length === 0)) {
      onClose?.();
    }
  }, [loading, promotions, onClose]);

  if (loading || !promotions || promotions.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[340] flex items-start justify-center overflow-y-auto overscroll-contain p-4 safe-top safe-bottom sm:p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-secondary-950/55 backdrop-blur-[6px]"
      />

      <div className="pointer-events-none fixed inset-0 z-[345]">
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={110}
          gravity={0.28}
          colors={['#f97316', '#fb923c', '#fdba74', '#ffffff']}
        />
      </div>

      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="promotions-intro-title"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease }}
        className="relative z-[350] my-auto w-full max-w-lg overflow-hidden rounded-2xl border border-secondary-200/70 bg-white shadow-[0_24px_64px_rgba(15,23,42,0.22)] sm:max-w-2xl"
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label={copy.dismiss || 'Close'}
          className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full text-secondary-400 transition hover:bg-secondary-100 hover:text-secondary-700"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="px-6 py-8 sm:px-12 sm:py-10">
          <div className="mx-auto flex w-full flex-col items-center text-center">
            <span className="icon-tile h-12 w-12 from-orange-500 to-amber-500 shadow-soft">
              <TrophyIcon className="h-6 w-6 text-white" strokeWidth={1.75} />
            </span>
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-orange-600 sm:text-xs">
              {copy.eyebrow || 'Promotions'}
            </p>
            <h2
              id="promotions-intro-title"
              className="mt-2 text-2xl font-semibold tracking-tight text-secondary-900 sm:text-3xl"
            >
              {copy.title || 'You can start winning prizes!'}
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-secondary-600 sm:text-base">
              {copy.subtitle || 'Refer new people and join the Elevate Hispano raffles.'}
            </p>
          </div>

          <PromoCarousel promotions={promotions} copy={copy} formatMessage={formatMessage} />

          <div className="mt-7 flex flex-col items-center gap-3">
            <Button
              type="button"
              onClick={handleView}
              size="large"
              icon={SparklesIcon}
              className="w-full max-w-sm sm:max-w-md"
            >
              {copy.viewCta || 'View promotions'}
            </Button>
            <button
              type="button"
              onClick={handleClose}
              className="text-sm font-medium text-secondary-500 transition hover:text-secondary-800"
            >
              {copy.dismiss || 'Maybe later'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default PromotionsIntroModal;
